import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import styles from "./DeliverableSidePanel.module.css";
import { CustomButton, createNotification } from "../../../../common/components";
import { translate } from "../../../../common/providers";
import CategoriesTreeView from "../categories-view/CategoriesTreeView";
import CoverageDocumentService from "../../../../api/services/operation/coverage-document-service";
import { ApiService } from "../../../../api";
import { debounce, isExcelMimeTypes } from "../../../../common/utils";
import { initialState, initialErrorState } from "./utils";
import ImportView from "./ImportView";
import FormView from "./FormView";

export default function DeliverableSidePanel({
	action,
	document = null,
	fbs = [],
	formats = [],
	mode = null,
	onClose,
	onExpand,
	onImportSuccess,
	owners = [],
	phases = [],
	projectId,
	types = [],
}) {
	const [errors, setErrors] = useState(initialErrorState);
	const [payload, setPayload] = useState(initialState(document, owners, phases));
	const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
	const [hasCategoriesBeenOpened, setHasCategoriesBeenOpened] = useState(false);
	const [editModeCategories, setEditModeCategories] = useState([]);
	const [isRefDuplicate, setIsRefDuplicate] = useState(false);
	const [isNameDuplicate, setIsNameDuplicate] = useState(false);
	const isFormValid =
		payload.name?.length > 2 &&
		payload.ref &&
		payload.ownerId &&
		payload.type &&
		payload.format &&
		payload.phaseId &&
		!isRefDuplicate &&
		!isNameDuplicate;

	const [selectedFile, setSelectedFile] = useState(null);
	const [importErrors, setImportErrors] = useState(null);
	const [shouldErase, setShouldErase] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [templateHeaders, setTemplateHeaders] = useState(null);
	const [showTemplateTable, setShowTemplateTable] = useState(false);
	const fileInputRef = useRef(null);
	const duplicateCancelSrcRef = useRef(null);
	const isMountedRef = useRef(true);
	const debouncedCheckDuplicates = useMemo(
		() =>
			debounce(async ({ ref, name }) => {
				if (!projectId) {
					return;
				}
				if (mode !== "add") {
					return;
				}
				const refTrimmed = typeof ref === "string" ? ref.trim() : "";
				const nameTrimmed = typeof name === "string" ? name.trim() : "";

				if (duplicateCancelSrcRef.current && duplicateCancelSrcRef.current.cancel) {
					duplicateCancelSrcRef.current.cancel("canceled by new request");
				}

				const cancelSource = ApiService.getCancelTokenSource();
				duplicateCancelSrcRef.current = cancelSource;
				try {
					if (refTrimmed) {
						try {
							const refResponse = await CoverageDocumentService.existsByProjectAndRef(
								{ projectId },
								{ ref: refTrimmed },
								cancelSource.token
							);
							const refExists = refResponse.value === true || refResponse.value === "true";
							setIsRefDuplicate(Boolean(refExists));
						} catch (err) {
							console.error("Error checking ref duplicate:", err);
						}
					} else {
						setIsRefDuplicate(false);
					}

					if (nameTrimmed && nameTrimmed.length >= 3) {
						try {
							const nameResponse = await CoverageDocumentService.existsByProjectAndName(
								{ projectId },
								{ name: nameTrimmed },
								cancelSource.token
							);
							const nameExists = nameResponse.value === true || nameResponse.value === "true";
							setIsNameDuplicate(Boolean(nameExists));
						} catch (err) {
							console.error("Error checking name duplicate:", err);
						}
					} else {
						setIsNameDuplicate(false);
					}
				} catch (err) {
					console.error("Error checking duplicates:", err);
				} finally {
					duplicateCancelSrcRef.current = null;
				}
			}, 300),
		[mode, projectId]
	);

	const checkDuplicates = useCallback(
		({ ref, name }) => {
			if (!projectId) {
				return;
			}
			if (mode !== "add") {
				return;
			}
			debouncedCheckDuplicates({ ref, name });
		},
		[mode, projectId, debouncedCheckDuplicates]
	);

	const changeName = useCallback(
		(name) => {
			if (!name || name.length < 3) {
				setErrors((prev) => ({ ...prev, name: true }));
			} else if (errors.name) {
				setErrors((prev) => ({ ...prev, name: false }));
			}
			setPayload((prev) => ({ ...prev, name }));
			checkDuplicates({ ref: payload.ref, name });
		},
		[errors.name, checkDuplicates, payload.ref]
	);

	const changeMandatoryFields = useCallback(
		(content, field) => {
			if (!content) {
				setErrors((prev) => ({ ...prev, [field]: true }));
			} else if (errors[field]) {
				setErrors((prev) => ({ ...prev, [field]: false }));
			}
			setPayload((prev) => ({ ...prev, [field]: content }));
			if (field === "ref") {
				checkDuplicates({ ref: content, name: payload.name });
			}
		},
		[errors, checkDuplicates, payload.name]
	);

	const changeNullableFields = useCallback((content, field) => {
		setPayload((prev) => ({ ...prev, [field]: content }));
	}, []);

	const reset = () => {
		setErrors(initialErrorState);
		setPayload(initialState(document, owners, phases));
		setHasCategoriesBeenOpened(false);
		setIsCategoriesOpen(false);
	};

	const handleCheckCategory = useCallback((checkedElements) => {
		setPayload((prev) => ({ ...prev, thematics: checkedElements }));
	}, []);
	const handleEditModeCategories = useCallback((checkedIds) => {
		setEditModeCategories(checkedIds);
	}, []);

	const handleSubmit = () => {
		const errs = {
			name: !(payload.name && payload.name.length > 2),
			ref: !payload.ref,
			owner: !payload.ownerId,
			type: !payload.type,
			format: !payload.format,
			phaseId: !payload.phaseId,
		};

		if (!Object.keys(errs).some((key) => errs[key])) {
			let thematicsValue;
			if (mode === "edit") {
				thematicsValue = hasCategoriesBeenOpened ? editModeCategories : null;
			} else {
				thematicsValue = payload.thematics;
			}

			const finalPayload = {
				...payload,
				thematics: thematicsValue,
			};

			action(finalPayload);
			onClose();
		}

		setErrors(errs);
	};

	const handleCancel = () => {
		reset();
		onClose();
	};

	const handleKeyDown = useCallback(
		(callback) => (e) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				callback();
			}
		},
		[]
	);

	const handleImportFile = async (file) => {
		if (!file || !projectId) {
			return;
		}
		setIsUploading(true);
		const cancelTokenSource = ApiService.getCancelTokenSource();
		try {
			const response = await CoverageDocumentService.import(
				{ projectId },
				file,
				shouldErase,
				cancelTokenSource.token
			);

			if (response?.valid === false && response?.errors?.length > 0) {
				setImportErrors(response.errors.slice(0, 3));
			} else {
				setImportErrors(null);
				createNotification({
					type: "success",
					message: translate("deliverables-list.success.import_complete"),
				});
				if (onImportSuccess) {
					onImportSuccess();
				}
				onClose();
			}
		} catch (error) {
			console.error("Error importing file:", error);
			if (error.response?.data) {
				const errorData = error.response.data;
				if (errorData.valid === false && errorData.errors?.length > 0) {
					setImportErrors(errorData.errors.slice(0, 3));
				} else {
					setImportErrors([translate("deliverables-list.error.import_failed")]);
				}
			} else {
				setImportErrors([translate("deliverables-list.error.import_failed")]);
			}
		} finally {
			setIsUploading(false);
		}
	};

	const handleFileSelect = useCallback((event) => {
		const file = event.target.files?.[0];
		if (file) {
			if (!isExcelMimeTypes(file.type) && !file.name.endsWith(".xls") && !file.name.endsWith(".xlsx")) {
				return;
			}
			setSelectedFile(file);
			setImportErrors(null);
		}
	}, []);

	const handleShowTemplate = async () => {
		if (!projectId) {
			return;
		}
		const cancelTokenSource = ApiService.getCancelTokenSource();
		try {
			const response = await CoverageDocumentService.getImportTemplate({ projectId }, cancelTokenSource.token);
			setTemplateHeaders(response);
			setShowTemplateTable(true);
		} catch (error) {
			console.error("Error fetching template:", error);
			createNotification({
				type: "error",
				message: translate("deliverables-list.error.download_template"),
			});
		}
	};

	const handleDownloadTemplate = async () => {
		if (!projectId) {
			return;
		}
		const cancelTokenSource = ApiService.getCancelTokenSource();
		try {
			const response = await CoverageDocumentService.getImportTemplateFile(
				{ projectId },
				cancelTokenSource.token
			);
			const contentDisposition = response.headers?.["content-disposition"];
			let filename = "coverage_import_template.xlsx";

			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (filenameMatch && filenameMatch[1]) {
					filename = filenameMatch[1].replace(/['"]/g, "");
				}
			}

			const url = window.URL.createObjectURL(new Blob([response.data || response]));
			const link = window.document.createElement("a");
			link.href = url;
			link.download = filename;
			window.document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Error downloading template:", error);
			createNotification({
				type: "error",
				message: translate("deliverables-list.error.download_template"),
			});
		}
	};

	const handleImport = async () => {
		if (!selectedFile || !projectId) {
			return;
		}
		await handleImportFile(selectedFile);
	};

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (!isMountedRef.current) {
			return;
		}
		setPayload(initialState(document, owners, phases));
		setHasCategoriesBeenOpened(false);
		setIsCategoriesOpen(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [document?.id, mode]);

	useEffect(() => {
		if (!isMountedRef.current || !document) {
			return;
		}

		setPayload((prev) => {
			const updated = { ...prev };
			let needsUpdate = false;

			if (owners.length > 0) {
				const currentOwnerExists = updated.ownerId && owners.find((o) => o.id === updated.ownerId);

				if (!currentOwnerExists) {
					if (document.owner?.id) {
						const matchingOwner = owners.find((o) => o.id === document.owner.id);
						if (matchingOwner?.id && matchingOwner.id !== updated.ownerId) {
							updated.ownerId = matchingOwner.id;
							needsUpdate = true;
						}
					} else if (document.ownerName) {
						const matchingOwner = owners.find(
							(o) => o.displayName === document.ownerName || o.name === document.ownerName
						);
						if (matchingOwner?.id && matchingOwner.id !== updated.ownerId) {
							updated.ownerId = matchingOwner.id;
							needsUpdate = true;
						}
					} else if (document.ownerId && !updated.ownerId) {
						const matchingOwner = owners.find((o) => o.id === document.ownerId);
						if (matchingOwner?.id) {
							updated.ownerId = matchingOwner.id;
							needsUpdate = true;
						}
					}
				}
			}

			if (phases.length > 0) {
				const currentPhaseExists = updated.phaseId && phases.find((p) => p.id === updated.phaseId);

				if (!currentPhaseExists) {
					if (document.phase?.id) {
						const matchingPhase = phases.find((p) => p.id === document.phase.id);
						if (matchingPhase?.id && matchingPhase.id !== updated.phaseId) {
							updated.phaseId = matchingPhase.id;
							needsUpdate = true;
						}
					} else if (document.phaseName) {
						const matchingPhase = phases.find((p) => p.name === document.phaseName);
						if (matchingPhase?.id && matchingPhase.id !== updated.phaseId) {
							updated.phaseId = matchingPhase.id;
							needsUpdate = true;
						}
					} else if (document.phaseId && !updated.phaseId) {
						const matchingPhase = phases.find((p) => p.id === document.phaseId);
						if (matchingPhase?.id) {
							updated.phaseId = matchingPhase.id;
							needsUpdate = true;
						}
					}
				}
			}

			return needsUpdate ? updated : prev;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [owners.length, phases.length, document?.owner?.id, document?.phase?.id]);

	useEffect(
		() => () => {
			if (duplicateCancelSrcRef.current && duplicateCancelSrcRef.current.cancel)
				duplicateCancelSrcRef.current.cancel();
		},
		[]
	);

	const handleValidateView = useCallback(() => {
		action({ thematics: editModeCategories });
		onClose();
	}, [action, editModeCategories, onClose]);

	const handleToggleCategories = useCallback(() => {
		setIsCategoriesOpen((prev) => {
			if (!prev) {
				setHasCategoriesBeenOpened(true);
			}
			return !prev;
		});
	}, []);

	if (mode === "view") {
		const isViewValid = editModeCategories.length > 0;
		return (
			<div className={styles.sidePanelContent}>
				<div className={styles.viewCategoriesWrapper}>
					<CategoriesTreeView deliverableId={document?.id} onCategoriesChange={handleEditModeCategories} />
				</div>
				<div className={styles.actionButtons}>
					<CustomButton variant="outlined" onClick={handleCancel}>
						{translate("deliverables-list.actions.cancel")}
					</CustomButton>
					<CustomButton disabled={!isViewValid} variant="contained" onClick={handleValidateView}>
						{translate("deliverables-list.actions.validate")}
					</CustomButton>
				</div>
			</div>
		);
	}
	if (mode === "import") {
		return (
			<ImportView
				fileInputRef={fileInputRef}
				importErrors={importErrors}
				isUploading={isUploading}
				selectedFile={selectedFile}
				shouldErase={shouldErase}
				showTemplateTable={showTemplateTable}
				templateHeaders={templateHeaders}
				onCancel={handleCancel}
				onDownloadTemplate={handleDownloadTemplate}
				onFileSelect={handleFileSelect}
				onImport={handleImport}
				onKeyDown={handleKeyDown}
				onSetShouldErase={setShouldErase}
				onSetShowTemplateTable={setShowTemplateTable}
				onShowTemplate={handleShowTemplate}
			/>
		);
	}
	return (
		<div className={styles.sidePanelContent}>
			<div className={styles.formContainer}>
				<FormView
					document={document}
					errors={errors}
					fbs={fbs}
					formats={formats}
					isCategoriesOpen={isCategoriesOpen}
					isNameDuplicate={isNameDuplicate}
					isRefDuplicate={isRefDuplicate}
					mode={mode}
					owners={owners}
					payload={payload}
					phases={phases}
					types={types}
					onChangeMandatoryFields={changeMandatoryFields}
					onChangeName={changeName}
					onChangeNullableFields={changeNullableFields}
					onCheckCategory={handleCheckCategory}
					onEditModeCategories={handleEditModeCategories}
					onExpand={onExpand}
					onToggleCategories={handleToggleCategories}
				/>
			</div>
			<div className={styles.actionButtons}>
				<CustomButton variant="outlined" onClick={handleCancel}>
					{translate("deliverables-list.actions.cancel")}
				</CustomButton>
				<CustomButton disabled={!isFormValid} variant="contained" onClick={handleSubmit}>
					{translate("deliverables-list.actions.validate")}
				</CustomButton>
			</div>
		</div>
	);
}
